// Small "next turn ≈ N tok • $X" chip that sits above the composer textarea
// on the right. Always visible — even on a brand-new chat, where it shows
// the system+tools baseline cost (the chat id "new" makes the backend skip
// the chat-ownership check and return just the static prefix).
//
// Sources its value, in priority order, from:
//   1. A `_prefix_estimate` WS event the broker pushes after each turn
//      (carried via `useAltYChat`'s `prefixEstimate`).
//   2. A `GET /api/alty/chat/<id>/prefix-estimate` fetch keyed off the
//      current chat id + model — fires on mount, chat switch, model
//      change, and any time the broker reports a turn finished.
//
// Re-renders are cheap: the chip text is two numbers + a tooltip, no images
// or heavy children.
import { useEffect, useMemo, useState } from 'react';
import { getAltYApiBase } from '../lib/altYApi';
import { fetchWithAltYNativeAuth } from '../lib/nativeAuth';
import type { AltYPrefixEstimate } from '../lib/useAltYChat';

// Map the panel's coarse `model` token to a pricing-table key the broker
// understands. The broker also handles unknown keys via a default, so this
// is purely an optimisation to make the chip's number match what the next
// turn actually costs.
const MODEL_TO_PRICING_KEY: Record<string, string> = {
  claude: 'claude-opus-4-7',
  codex: 'gpt-5',
  gemini: 'gemini-3.1-pro',
  cerebras: 'qwen-3-235b',
  cursor: 'claude-sonnet-4-6',
  grok: 'glm-4-7',
};

function formatTokens(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n < 1000) return String(n);
  const k = n / 1000;
  // Below 100k: one decimal (12.4k). Above: no decimal (123k).
  return n < 100_000 ? `${k.toFixed(1)}k` : `${Math.round(k)}k`;
}

function formatCost(usd: number): string {
  if (!Number.isFinite(usd) || usd < 0) return '$0';
  if (usd < 0.01) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

type Props = {
  chatId: number | null;
  model: string;
  busy: boolean;
  // Latest broker push; takes priority over the fetched value.
  wsEstimate: AltYPrefixEstimate | null;
};

export default function PrefixCostChip({ chatId, model, busy, wsEstimate }: Props) {
  const [fetched, setFetched] = useState<AltYPrefixEstimate | null>(null);

  const pricingModel = MODEL_TO_PRICING_KEY[model] || model || 'claude-opus-4-7';
  // The backend treats "new" as "no chat yet → return the baseline".
  const chatToken = chatId == null ? 'new' : String(chatId);

  useEffect(() => {
    let aborted = false;
    fetchWithAltYNativeAuth(`${getAltYApiBase()}/chat/${chatToken}/prefix-estimate?model=${encodeURIComponent(pricingModel)}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (aborted) return;
        if (json && typeof json === 'object' && typeof json.tokens === 'number') {
          setFetched(json as AltYPrefixEstimate);
        }
      })
      .catch(() => {
        // Best-effort; the chip just stays at its previous value.
      });
    return () => {
      aborted = true;
    };
    // `busy` toggling to false signals "turn finished" — re-fetch to pick
    // up the new baseline even on browsers that miss the ws push (replay,
    // background tab throttling, etc).
  }, [chatToken, pricingModel, busy]);

  // WS estimate wins when it's for the same model the chip is currently
  // showing; otherwise the fetched value (which IS for the right model)
  // is the source of truth.
  const value: AltYPrefixEstimate | null = useMemo(() => {
    if (wsEstimate && wsEstimate.model === pricingModel) return wsEstimate;
    return fetched;
  }, [wsEstimate, fetched, pricingModel]);

  if (!value) {
    // Reserve the space so the layout doesn't jump when the first response
    // lands. An empty span renders nothing visible.
    return <span className="alty-prefix-cost-chip" aria-hidden="true" />;
  }

  const tokens = formatTokens(value.tokens);
  const cost = formatCost(value.estimated_cost_usd);
  const br = value.breakdown || { system_tokens: 0, summaries_tokens: 0, verbatim_tokens: 0 };
  const ratio = value.last_turn_cache_hit_ratio;
  const tooltip = [
    `system + tools  ${formatTokens(br.system_tokens)} tok`,
    `summaries       ${formatTokens(br.summaries_tokens)} tok`,
    `verbatim        ${formatTokens(br.verbatim_tokens)} tok`,
    '─────────────────────────',
    `total          ${formatTokens(value.tokens)} tok @ $${value.input_price_per_mtok}/M (${value.model}${
      ratio == null ? ', no cache data yet' : `, cache ${Math.round(ratio * 100)}% from last turn`
    })`,
  ].join('\n');

  return (
    <span
      className="alty-prefix-cost-chip"
      title={tooltip}
      data-tip={tooltip.replace(/\n/g, ' · ')}
    >
      {tokens} tok • {cost}
    </span>
  );
}
